// The first thing somebody sees after opening a team invite, on top of their own
// home: the name the company sent, to confirm or correct, and a password. It cannot be waved away. The invite link stops working the moment a
// password is set and nothing else signs them in, so leaving without one would
// leave them locked out of their own account until a reset.
import React, { useEffect, useState } from "react";
import useDialog from "@/hooks/useDialog";
import { toast } from "react-hot-toast";
import Field from "../ui/Field";
import { fetchWithAuth } from "../../utils/fetchWithAuth";
import { readInviteArrival, updateInviteArrival } from "./inviteArrival";

const inputCls =
  "w-full px-4 py-3 rounded-xl text-base outline-none focus:ring-2 focus:ring-[var(--brand)]";
const inputStyle = { background: "var(--surface)", border: "1px solid var(--border-strong)", color: "var(--ink)" };

export default function SetPasswordPrompt() {
  const [arrival, setArrival] = useState(() => readInviteArrival());
  const [first, setFirst] = useState("");
  const [last, setLast] = useState("");
  const [pw, setPw] = useState("");
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  // Prefill with the name the company typed, so the person confirms rather
  // than retypes it.
  useEffect(() => {
    if (!arrival?.needsPassword) return;
    fetchWithAuth("/api/accounts/me/")
      .then((me) => {
        setFirst((v) => v || me?.first_name || "");
        setLast((v) => v || me?.last_name || "");
      })
      .catch(() => {});
  }, [arrival?.needsPassword]);

  // Required: no Escape. Focus and Tab stay in the form.
  const dialog = useDialog({ open: !!arrival?.needsPassword });

  if (!arrival?.needsPassword) return null;

  const submit = async (e) => {
    e.preventDefault();
    const next = {};
    if (!first.trim()) next.first = "Enter your first name.";
    if (!last.trim()) next.last = "Enter your last name.";
    if (pw.length < 8) next.pw = "Use at least 8 characters.";
    setErrors(next);
    if (Object.keys(next).length) return;
    setSaving(true);
    try {
      await fetchWithAuth("/api/accounts/set-password/", {
        method: "POST",
        json: { password: pw, first_name: first.trim(), last_name: last.trim() },
      });
      updateInviteArrival({ needsPassword: false });
      setArrival((a) => ({ ...a, needsPassword: false }));
      toast.success("You're all set. Sign in with your email and this password from now on.");
    } catch (err) {
      const msg = err?.data?.password?.[0] || err?.message || "That password didn't work. Try another.";
      setErrors({ pw: msg });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center px-4" style={{ background: "rgba(10, 20, 15, 0.55)" }}>
      <form
        onSubmit={submit}
        ref={dialog.ref}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="set-password-title"
        className="w-full max-w-md rounded-2xl p-6 sm:p-8 shadow-xl"
        style={{ background: "var(--surface)", color: "var(--ink)" }}
      >
        <h2 id="set-password-title" className="text-2xl font-semibold mb-1">
          Welcome{arrival.orgName ? ` to ${arrival.orgName}` : ""}
        </h2>
        <p className="text-sm mb-5" style={{ color: "var(--text-secondary)" }}>
          Check your name is right, then choose a password. You'll use your email and this password to sign in next time.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
          <Field label="First name" error={errors.first}>
            <input className={inputCls} style={inputStyle} autoComplete="given-name" value={first} onChange={(e) => setFirst(e.target.value)} />
          </Field>
          <Field label="Last name" error={errors.last}>
            <input className={inputCls} style={inputStyle} autoComplete="family-name" value={last} onChange={(e) => setLast(e.target.value)} />
          </Field>
        </div>
        <Field label="Password" hint="At least 8 characters." error={errors.pw}>
          <input
            type={show ? "text" : "password"}
            className={inputCls}
            style={inputStyle}
            autoComplete="new-password"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
          />
        </Field>
        <label className="flex items-center gap-2 text-sm mt-2 mb-5" style={{ color: "var(--text-secondary)" }}>
          <input type="checkbox" aria-label="Show password" checked={show} onChange={(e) => setShow(e.target.checked)} />
          Show password
        </label>
        <button
          type="submit"
          disabled={saving}
          className="w-full py-3 rounded-full font-semibold disabled:opacity-60"
          style={{ background: "var(--brand)", color: "#fff", minHeight: 48 }}
        >
          {saving ? "Saving…" : "Save and continue"}
        </button>
      </form>
    </div>
  );
}
